import { useState } from "react";
import { apiChangePassword, apiForgotPassword } from "../../services/authService";
import { isValidPassword } from "../../utils/validation";
import "../../App.css";

export default function AccountPasswordSection({ user }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const lengthOk = isValidPassword(newPassword);
  const matchOk = newPassword.length > 0 && newPassword === confirmPassword;

  function resetForm() {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setSuccess(false);
    if (!lengthOk) { setError("New password must be at least 8 characters."); return; }
    if (!matchOk) { setError("Passwords do not match."); return; }

    setSaving(true);
    try {
      await apiChangePassword(currentPassword, newPassword);
      setSuccess(true);
      resetForm();
    } catch (err) {
      if (err.errorCode === "invalid_current_password") {
        setError("Current password is incorrect.");
      } else {
        setError(err.message || "Could not change password.");
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleForgot() {
    setForgotLoading(true);
    try {
      await apiForgotPassword(user.email);
      setForgotSent(true);
    } finally {
      setForgotLoading(false);
    }
  }

  function handleCancel() {
    resetForm();
    setError("");
    setSuccess(false);
    setIsOpen(false);
  }

  if (!isOpen) {
    return (
      <div className="account-group">
        <h4 className="account-group-label">Security</h4>
        <button className="account-group-row" onClick={() => setIsOpen(true)}>
          <span>Password</span>
          <span className="account-row-action">Change Password ›</span>
        </button>
      </div>
    );
  }

  return (
    <div className="account-card">
      <h3 className="account-card-title">Change Password</h3>
      <form className="account-password-form" onSubmit={handleSubmit}>
        <div className="auth-field">
          <label className="auth-label" htmlFor="current-password">Current Password</label>
          <input
            id="current-password"
            className="auth-input"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
          />
          {forgotSent ? (
            <p className="account-forgot-sent">Reset link sent — check your inbox.</p>
          ) : (
            <button type="button" className="auth-forgot-link" onClick={handleForgot} disabled={forgotLoading}>
              {forgotLoading ? "Sending…" : "Forgot your current password?"}
            </button>
          )}
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="new-password">New Password</label>
          <input
            id="new-password"
            className="auth-input"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <div className="auth-field">
          <label className="auth-label" htmlFor="confirm-new-password">Confirm New Password</label>
          <input
            id="confirm-new-password"
            className="auth-input"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
          />
        </div>

        <ul className="password-checklist">
          <li className={lengthOk ? "met" : ""}>{lengthOk ? "✓" : "✗"} At least 8 characters</li>
          <li className={matchOk ? "met" : ""}>{matchOk ? "✓" : "✗"} Passwords match</li>
        </ul>

        {error && <p className="auth-error">{error}</p>}
        {success && <p className="account-success-text">Password changed successfully.</p>}

        <div className="account-username-edit-actions">
          <button className="regen-btn" type="submit" disabled={saving}>
            {saving ? "Changing…" : "Change Password"}
          </button>
          <button type="button" className="account-cancel-link" onClick={handleCancel} disabled={saving}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
